import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { CreateRoleRequest, Role, UpdateRoleRequest } from '@/models/role.ts';

export const useRoles = () => {
    const queryClient = useQueryClient();

    const {
        data: roles,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['roles'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<Role[]>>(API_ENDPOINTS.CRM.ROLES);
            return data.data;
        },
    });

    const createRole = useMutation({
        mutationFn: async (data: CreateRoleRequest) => {
            const { data: response } = await httpClient.post<ApiResponse<Role>>(
                API_ENDPOINTS.CRM.ROLES,
                data
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] });
        },
    });

    const updateRole = useMutation({
        mutationFn: async ({ id, ...data }: UpdateRoleRequest) => {
            const { data: response } = await httpClient.put<ApiResponse<Role>>(
                `${API_ENDPOINTS.CRM.ROLES}/${id}`,
                data
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] });
        },
    });

    const deleteRole = useMutation({
        mutationFn: async (id: string) => {
            await httpClient.delete(`${API_ENDPOINTS.CRM.ROLES}/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] });
        },
    });

    return {
        roles,
        isLoading,
        error,
        createRole,
        updateRole,
        deleteRole,
    };
};
